import React, { useState, useEffect } from 'react';
import { FiUpload, FiDownload, FiAlertTriangle } from 'react-icons/fi';
import api from '../services/api';
import '../components/Button.css';
import './PayPalMt940Conversie.css';

const STORAGE_KEY_ACCOUNT25 = 'inkoop_paypal_mt940_account25';

function readStoredAccount25() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    return window.localStorage.getItem(STORAGE_KEY_ACCOUNT25) || '';
  } catch {
    return '';
  }
}

function Mt940SuccessSummary({
  lastCount,
  wooMatchedShopify,
  wooMatchedWooInvoice,
  wooMatchedEmail,
  wooMatchedName,
  wooMatchedAddr,
}) {
  const sh = wooMatchedShopify ?? 0;
  const wInv = wooMatchedWooInvoice ?? 0;
  const em = wooMatchedEmail ?? 0;
  const nm = wooMatchedName ?? 0;
  const ad = wooMatchedAddr ?? 0;
  const inv = sh + wInv;
  const wooTotal = inv + em + nm + ad;
  return (
    <>
      <p className="paypal-mt940-success">
        Bestand gedownload met <strong>{lastCount}</strong> transactieregel(s).
      </p>
      <p className="paypal-mt940-success paypal-mt940-success--woo-total">
        <strong>{wooTotal}</strong> van <strong>{lastCount}</strong> transacties gekoppeld aan een WooCommerce-ordernummer
        {wooTotal < lastCount && (
          <>
            {' '}
            (<strong>{lastCount - wooTotal}</strong> zonder Woo-koppeling)
          </>
        )}
        .
      </p>
      {(sh > 0 || wInv > 0 || em > 0 || nm > 0 || ad > 0) && (
        <p className="paypal-mt940-success paypal-mt940-success--detail">
          Verdeling:{' '}
          {sh > 0 && (
            <>
              <strong>{sh}</strong> via Shopify-mapping (factuurveld)
              {(wInv > 0 || em > 0 || nm > 0 || ad > 0) && ', '}
            </>
          )}
          {wInv > 0 && (
            <>
              <strong>{wInv}</strong> via Woo-order­nr in factuur (verkoopdata / directe Woo)
              {(em > 0 || nm > 0 || ad > 0) && ', '}
            </>
          )}
          {em > 0 && (
            <>
              <strong>{em}</strong> via klant-e-mail + bedrag
              {(nm > 0 || ad > 0) && ', '}
            </>
          )}
          {nm > 0 && (
            <>
              <strong>{nm}</strong> via klantnaam + bedrag (Woo/Shopify-naam uit verkoopdata)
              {ad > 0 && ', '}
            </>
          )}
          {ad > 0 && (
            <>
              <strong>{ad}</strong> via postcode + land + bedrag
            </>
          )}
          .
        </p>
      )}
    </>
  );
}

export default function PayPalMt940Conversie() {
  const [file, setFile] = useState(null);
  const [account25, setAccount25] = useState(() => readStoredAccount25());
  const [openingBalance, setOpeningBalance] = useState('');
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [error, setError] = useState(null);
  const [lastCount, setLastCount] = useState(null);
  const [wooMatchedShopify, setWooMatchedShopify] = useState(null);
  const [wooMatchedWooInvoice, setWooMatchedWooInvoice] = useState(null);
  const [wooMatchedEmail, setWooMatchedEmail] = useState(null);
  const [wooMatchedName, setWooMatchedName] = useState(null);
  const [wooMatchedAddr, setWooMatchedAddr] = useState(null);

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const v = account25.trim();
      if (v) window.localStorage.setItem(STORAGE_KEY_ACCOUNT25, v);
      else window.localStorage.removeItem(STORAGE_KEY_ACCOUNT25);
    } catch {
      /* ignore quota / private mode */
    }
  }, [account25]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setError(null);
    setWarnings([]);
    setLastCount(null);
    setWooMatchedShopify(null);
    setWooMatchedWooInvoice(null);
    setWooMatchedEmail(null);
    setWooMatchedName(null);
    setWooMatchedAddr(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Selecteer eerst een CSV-bestand (PayPal-export).');
      return;
    }
    setBusy(true);
    setError(null);
    setWarnings([]);
    setLastCount(null);
    setWooMatchedShopify(null);
    setWooMatchedWooInvoice(null);
    setWooMatchedEmail(null);
    setWooMatchedName(null);
    setWooMatchedAddr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (account25.trim()) fd.append('account25', account25.trim());
      if (openingBalance.trim() !== '') fd.append('openingBalance', openingBalance.trim());

      const res = await api.post('/paypal-mt940/convert', fd);
      const {
        mt940,
        warnings: w = [],
        count,
        filename,
        shopify_woo_matched: wooShopify,
        woo_matched_verkoop_invoice: wooVcInv,
        woo_matched_email_amount: wooEm,
        woo_matched_name_amount: wooNm,
        woo_matched_address_amount: wooAd,
      } = res.data || {};
      if (!mt940) {
        throw new Error('Geen MT940-tekst ontvangen.');
      }
      setWarnings(Array.isArray(w) ? w : []);
      setLastCount(typeof count === 'number' ? count : null);
      setWooMatchedShopify(typeof wooShopify === 'number' ? wooShopify : null);
      setWooMatchedWooInvoice(typeof wooVcInv === 'number' ? wooVcInv : null);
      setWooMatchedEmail(typeof wooEm === 'number' ? wooEm : null);
      setWooMatchedName(typeof wooNm === 'number' ? wooNm : null);
      setWooMatchedAddr(typeof wooAd === 'number' ? wooAd : null);

      const blob = new Blob([mt940], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'paypal-export.mt940';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        (typeof err.response?.data === 'string' ? err.response.data : null) ||
        err.message ||
        'Conversie mislukt.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="paypal-mt940-page">
      <div className="paypal-mt940-card">
        <h2 className="paypal-mt940-title">
          <FiUpload /> PayPal CSV → MT940
        </h2>
        <p className="paypal-mt940-intro">
          Upload het standaard PayPal-exportbestand (kolommen zoals Datum, Net, Bruto, Fee/provisie, Status, Transactie-id).
          In <strong>:61:</strong> staat bij inkomende betalingen het <strong>bruto</strong> bedrag (kolom <strong>Bruto</strong>). Is er een PayPal-<strong>fee</strong> en klopt bruto − fee ≈ netto, dan volgt een <strong>tweede :61:-regel</strong> met het fee-bedrag als <strong>debet</strong>. De eerste regel heeft in <strong>:86:</strong> bestelnr, naam en «Kosten … EUR»; de fee-regel alleen <strong>Kosten Paypal</strong> — handig om in Exact automatisch naar het juiste grootboek te routeren. Het PayPal-saldo <strong>:62F:</strong> wordt nog steeds opgebouwd uit <strong>netto</strong> per transactie (som van alle :61:-bewegingen = netto).
          Als kolom <strong>Net</strong> gelijk is aan <strong>Bruto</strong> maar er is een <strong>fee</strong>, corrigeren we het interne netto met bruto − fee voor dat saldo.
          Als PayPal in <strong>Factuurnummer</strong> het <strong>WooCommerce-order­nummer of -ID</strong> zet (directe Woo-bestelling), koppelen we dat aan verkoopdata zodra die order daar is geïmporteerd. Lange PayPal-interne codes blijven verborgen op de :86:-regel; dan volgen <strong>Shopify-mapping</strong>, <strong>e-mail + bedrag</strong>, <strong>klantnaam + bedrag</strong> (PayPal «Naam» tegen <strong>klant_naam</strong> in verkoopdata — zelfde als op de Woo/Shopify-order na import; woorden gesorteerd vergelijkbaar, accenten genegeerd) en <strong>postcode + land + bedrag</strong>.
          Orders die je via <strong>Shopify → Woo</strong> hebt geïmporteerd, worden waar mogelijk aan het <strong>WooCommerce-order­nummer</strong> gekoppeld via de mapping in deze software (vóór de pure Woo-factuur­match).
          Het bedrag in verkoopdata is de <strong>som van productregels</strong> (zonder verzendlijn uit Woo); PayPal-<strong>Bruto</strong> is vaak hoger — daarvoor zijn twee zoek­rondes met ruimere marges (o.a. tot ca. €180 verschil en €50 «dichtstbij»). Bij gelijke kandidaten na gap-match wordt de order gekozen die het dichtst bij de PayPal-datum ligt.
          Alleen regels met status <strong>Voltooid</strong> worden meegenomen. Controleer het resultaat in je
          boekhoudpakket — MT940-varianten verschillen per software.
        </p>

        <form className="paypal-mt940-form" onSubmit={handleSubmit}>
          <label className="paypal-mt940-label">
            CSV-bestand
            <input type="file" accept=".csv,text/csv" onChange={handleFile} disabled={busy} />
          </label>

          <label className="paypal-mt940-label">
            Rekening (:25:) — optioneel (IBAN of dummy zoals <code>/EUR000000000000000000</code>)
            <input
              type="text"
              value={account25}
              onChange={(e) => setAccount25(e.target.value)}
              placeholder="bijv. NL91ABNA0417164300 of leeg voor placeholder"
              disabled={busy}
              autoComplete="off"
            />
            <span className="paypal-mt940-hint">Wordt op dit apparaat onthouden (browser).</span>
          </label>

          <label className="paypal-mt940-label">
            Openingssaldo EUR — optioneel (standaard 0)
            <input
              type="text"
              inputMode="decimal"
              value={openingBalance}
              onChange={(e) => setOpeningBalance(e.target.value)}
              placeholder="0"
              disabled={busy}
              autoComplete="off"
            />
          </label>

          <button type="submit" className="btn btn-primary paypal-mt940-submit" disabled={busy || !file}>
            <FiDownload /> {busy ? 'Bezig…' : 'Omzetten en downloaden'}
          </button>
        </form>

        {error && (
          <div className="paypal-mt940-msg paypal-mt940-msg--error" role="alert">
            <FiAlertTriangle /> {error}
          </div>
        )}

        {lastCount != null && !error && (
          <Mt940SuccessSummary
            lastCount={lastCount}
            wooMatchedShopify={wooMatchedShopify}
            wooMatchedWooInvoice={wooMatchedWooInvoice}
            wooMatchedEmail={wooMatchedEmail}
            wooMatchedName={wooMatchedName}
            wooMatchedAddr={wooMatchedAddr}
          />
        )}

        {warnings.length > 0 && (
          <div className="paypal-mt940-warnings">
            <h3>
              <FiAlertTriangle /> Waarschuwingen
            </h3>
            <ul>
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
